import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { useAccountTrialInfo } from '@hotjar/account';
import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { ERROR_REPORTING as ERROR_REPORTING_FF } from '@hotjar/recordings/src/constants/feature-flags';
import { TileIds as TILES } from '@hotjar/webapp/domains/activation/start-page/types';
import type { Site } from '@hotjar/site';
import { sessionsSettingsApi } from '@hotjar/webapp/features/Settings/api';

type SuggestionsFilters = {
  suggestionsFilters: (tile: TILES) => boolean;
};

export const useSuggestionsFilters = (site: Site): SuggestionsFilters => {
  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const isErrorCapturingFFEnabled = useFeatureFlag(ERROR_REPORTING_FF);
  const trialInfo = useAccountTrialInfo();
  const isScaleOrScaleTrial =
    site &&
    (trialInfo || undefined) &&
    isSiteOnScaleOrScaleTrial({ ...site, trial: { active: isOnActiveOrEligibleTrial(trialInfo) } });

  const { data: isCapturingErrors } = useQuery(
    'session-settings',
    () => sessionsSettingsApi.get(site.id),
    {
      enabled: !!site,
      select: (response) => response?.data?.session_capture_console_consent === true,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const suggestionsFilters = useCallback(
    (tile: TILES) => {
      if (tile === TILES.ERROR_REPORTING) {
        if (!isErrorCapturingFFEnabled) {
          return false;
        }

        if (!isScaleOrScaleTrial) {
          return false;
        }

        if (isCapturingErrors !== false) {
          return false;
        }
      }

      return true;
    },
    [isErrorCapturingFFEnabled, isScaleOrScaleTrial, isCapturingErrors],
  );

  return {
    suggestionsFilters,
  };
};
